import { useState } from 'react';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerHeaderNavigation,
  Button,
  Badge,
  Text,
  Divider,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular, Mail24Regular, Call24Regular, Delete24Regular } from '@fluentui/react-icons';
import dayjs from 'dayjs';
import { useAuth } from '@/components/auth/AuthProvider';
import { StatusBadge } from '@/components/referral/StatusBadge';
import { QuickStatusActions } from '@/components/admin/QuickStatusActions';
import { AdminNotesSection } from '@/components/admin/AdminNotesSection';
import { AssignmentSection } from '@/components/admin/AssignmentSection';
import { ActivityTimeline } from '@/components/admin/ActivityTimeline';
import { CommissionSection } from '@/components/admin/CommissionSection';
import { CommentThread } from '@/components/shared/CommentThread';
import { useDeleteReferral } from '@/api/hooks/useReferrals';
import type { ReferralDto } from '@/types/referral';

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  row: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '140px',
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  serviceList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  contactLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorBrandForeground1,
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  comments: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: 'pre-wrap',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  deleteSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

interface ReferralDetailDrawerProps {
  referral: ReferralDto | null;
  open: boolean;
  onClose: () => void;
}

export function ReferralDetailDrawer({ referral, open, onClose }: ReferralDetailDrawerProps) {
  const styles = useStyles();
  const { userEmail, userName } = useAuth();
  const deleteReferral = useDeleteReferral();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!referral) return null;

  const performedBy = userEmail ?? 'unknown';
  const performedByName = userName ?? 'Unknown';

  return (
    <OverlayDrawer position="end" size="medium" open={open} onOpenChange={(_e, d) => { if (!d.open) onClose(); }}>
      <DrawerHeader>
        <DrawerHeaderNavigation>
          <Button
            appearance="subtle"
            aria-label="Close"
            icon={<Dismiss24Regular />}
            onClick={onClose}
          />
        </DrawerHeaderNavigation>
        <DrawerHeaderTitle>
          <div className={styles.headerRow}>
            <Text weight="semibold" size={500}>
              {referral.clientContactName}
            </Text>
            <StatusBadge status={referral.status} />
          </div>
          {referral.businessName && (
            <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
              {referral.businessName}
            </Text>
          )}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        <div className={styles.body}>
          {/* Contact Info */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Contact Information</Text>
            <div className={styles.row}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Client</Text>
                <Text size={300}>{referral.clientContactName}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Email</Text>
                <a href={`mailto:${referral.clientEmail}`} className={styles.contactLink}>
                  <Mail24Regular style={{ fontSize: '16px' }} />
                  {referral.clientEmail}
                </a>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Phone</Text>
                <a href={`tel:${referral.clientPhone}`} className={styles.contactLink}>
                  <Call24Regular style={{ fontSize: '16px' }} />
                  {referral.clientPhone}
                </a>
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Referrer</Text>
                <Text size={300}>{referral.referrerName}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Referrer Email</Text>
                <a href={`mailto:${referral.referrerEmail}`} className={styles.contactLink}>
                  <Mail24Regular style={{ fontSize: '16px' }} />
                  {referral.referrerEmail}
                </a>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Location</Text>
                <Text size={300}>{referral.referrerLocation}</Text>
              </div>
            </div>
            {referral.isExistingClient && referral.clientNumber && (
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Client Number</Text>
                <Text size={300}>{referral.clientNumber}</Text>
              </div>
            )}
          </div>

          <Divider />

          {/* Opportunity Details */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Opportunity Details</Text>
            <div className={styles.field}>
              <Text className={styles.fieldLabel}>Services</Text>
              <div className={styles.serviceList}>
                {referral.services.map((svc) => (
                  <Badge key={svc} appearance="tint" color="informative" size="small">
                    {svc}
                  </Badge>
                ))}
              </div>
            </div>
            {referral.referrerComments && (
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Referrer Comments</Text>
                <div className={styles.comments}>
                  <Text size={300}>{referral.referrerComments}</Text>
                </div>
              </div>
            )}
            <div className={styles.row}>
              {referral.revenue != null && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Revenue</Text>
                  <Text size={300} style={{ color: tokens.colorPaletteGreenForeground1 }}>
                    ${referral.revenue.toLocaleString()}
                  </Text>
                </div>
              )}
              {referral.qualifiedDate && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Qualified Date</Text>
                  <Text size={300}>{dayjs(referral.qualifiedDate).format('MMM D, YYYY')}</Text>
                </div>
              )}
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Submitted</Text>
                <Text size={300}>{dayjs(referral.createdAt).format('MMM D, YYYY h:mm A')}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Last Updated</Text>
                <Text size={300}>{dayjs(referral.updatedAt).format('MMM D, YYYY h:mm A')}</Text>
              </div>
            </div>
          </div>

          <Divider />

          {/* Status & Quick Actions */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Status & Actions</Text>
            {referral.statusComments && (
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Status Notes</Text>
                <div className={styles.comments}>
                  <Text size={300}>{referral.statusComments}</Text>
                </div>
              </div>
            )}
            <QuickStatusActions
              referral={referral}
              performedBy={performedBy}
              performedByName={performedByName}
            />
          </div>

          <Divider />

          {/* Assignment */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Assignment</Text>
            <AssignmentSection
              referral={referral}
              performedBy={performedBy}
              performedByName={performedByName}
            />
          </div>

          <Divider />

          {/* Admin Notes */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Admin Notes</Text>
            <AdminNotesSection
              referral={referral}
              performedBy={performedBy}
              performedByName={performedByName}
            />
          </div>

          <Divider />

          {/* Commissions */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Commissions</Text>
            <CommissionSection
              referralId={referral.id}
              performedBy={performedBy}
              performedByName={performedByName}
            />
          </div>

          <Divider />

          {/* Comments */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Comments</Text>
            <CommentThread
              referralId={referral.id}
              authorEmail={performedBy}
              authorName={performedByName}
            />
          </div>

          <Divider />

          {/* Activity Timeline */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Activity History</Text>
            <ActivityTimeline referralId={referral.id} />
          </div>

          <Divider />

          {/* Delete Referral */}
          <div className={styles.section}>
            <div className={styles.deleteSection}>
              {!showDeleteConfirm ? (
                <Button
                  appearance="subtle"
                  icon={<Delete24Regular />}
                  style={{ color: tokens.colorPaletteRedForeground1 }}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Referral
                </Button>
              ) : (
                <>
                  <Text size={300} style={{ color: tokens.colorPaletteRedForeground1 }}>
                    Permanently delete this referral and all its data?
                  </Text>
                  <Button
                    appearance="primary"
                    style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                    disabled={deleteReferral.isPending}
                    onClick={async () => {
                      await deleteReferral.mutateAsync({
                        id: referral.id,
                        performedBy,
                        performedByName,
                      });
                      setShowDeleteConfirm(false);
                      onClose();
                    }}
                  >
                    {deleteReferral.isPending ? 'Deleting...' : 'Confirm Delete'}
                  </Button>
                  <Button
                    appearance="subtle"
                    disabled={deleteReferral.isPending}
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </DrawerBody>
    </OverlayDrawer>
  );
}
